import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/pos`;

const TaxSettings = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [taxGroups, setTaxGroups] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const [formData, setFormData] = useState({
    name: '',
    cgst: 0,
    sgst: 0,
    igst: 0,
    is_nil: false
  });

  const presets = [
    { name: 'GST 5%', cgst: 2.5, sgst: 2.5, igst: 5 },
    { name: 'GST 12%', cgst: 6, sgst: 6, igst: 12 },
    { name: 'GST 18%', cgst: 9, sgst: 9, igst: 18 },
    { name: 'GST 28%', cgst: 14, sgst: 14, igst: 28 }
  ];

  useEffect(() => {
    fetchTaxGroups();
  }, []);

  const fetchTaxGroups = async () => {
    try {
      const token = localStorage.getItem('pos_token');
      const response = await axios.get(`${API}/tax-groups`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTaxGroups(response.data);
    } catch (err) {
      setError('Failed to load tax groups');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : type === 'number' ? parseFloat(value) || 0 : value
    }));
  };

  const applyPreset = (preset) => {
    setFormData({
      name: preset.name,
      cgst: preset.cgst,
      sgst: preset.sgst,
      igst: preset.igst,
      is_nil: false
    });
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('pos_token');
      
      if (editingId) {
        await axios.put(`${API}/tax-groups/${editingId}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSuccess('Tax group updated successfully');
      } else {
        await axios.post(`${API}/tax-groups`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSuccess('Tax group created successfully');
      }
      
      fetchTaxGroups();
      resetForm();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save tax group');
    }
  };

  const handleEdit = (group) => {
    setFormData({
      name: group.name,
      cgst: group.cgst,
      sgst: group.sgst,
      igst: group.igst,
      is_nil: group.is_nil
    });
    setEditingId(group.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this tax group?')) return;

    try {
      const token = localStorage.getItem('pos_token');
      await axios.delete(`${API}/tax-groups/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess('Tax group deleted successfully');
      fetchTaxGroups();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to delete tax group');
    }
  };

  const seedDefaults = async () => {
    try {
      const token = localStorage.getItem('pos_token');
      const response = await axios.post(`${API}/tax-groups/seed-defaults`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess(response.data.message);
      fetchTaxGroups();
    } catch (err) {
      setError('Failed to seed default tax groups');
    }
  };

  const resetForm = () => {
    setFormData({ name: '', cgst: 0, sgst: 0, igst: 0, is_nil: false });
    setEditingId(null);
    setShowForm(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600"></div>
      </div>
    );
  }

  return (
    <div data-testid="tax-settings-page" className="max-w-4xl mx-auto">
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate('/gposp/settings')}
          className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
          data-testid="back-button"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Tax Settings (GST)</h1>
          <p className="text-slate-500">Configure GST rates and tax categories</p>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700">
          {error}
        </div>
      )}

      {success && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-xl text-green-700">
          {success}
        </div>
      )}

      {/* Quick Presets */}
      <div className="bg-white rounded-xl border border-slate-200 p-6 mb-6">
        <h2 className="text-lg font-semibold text-slate-800 mb-4">Quick Add (Standard GST Rates)</h2>
        <div className="flex flex-wrap gap-3">
          {presets.map((preset, idx) => (
            <button
              key={idx}
              onClick={() => applyPreset(preset)}
              className="px-4 py-2 bg-violet-50 text-violet-700 rounded-lg hover:bg-violet-100 transition-colors font-medium"
              data-testid={`preset-${preset.name.replace(' ', '-').toLowerCase()}`}
            >
              {preset.name}
            </button>
          ))}
          <button
            onClick={seedDefaults}
            className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors"
            data-testid="seed-defaults"
          >
            Add All Defaults
          </button>
        </div>
      </div>

      {/* Add/Edit Form */}
      {showForm && (
        <div className="bg-white rounded-xl border border-slate-200 p-6 mb-6">
          <h2 className="text-lg font-semibold text-slate-800 mb-4">
            {editingId ? 'Edit Tax Group' : 'Add Tax Group'}
          </h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Name</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  placeholder="e.g., GST 18%"
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                  data-testid="tax-name-input"
                />
              </div>
              <div className="flex items-center pt-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    name="is_nil"
                    checked={formData.is_nil}
                    onChange={handleChange}
                    className="w-4 h-4 text-violet-600 rounded"
                    data-testid="is-nil-checkbox"
                  />
                  <span className="text-slate-700">NIL Tax (No Tax)</span>
                </label>
              </div>
            </div>

            {!formData.is_nil && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">CGST %</label>
                  <input
                    type="number"
                    name="cgst"
                    value={formData.cgst}
                    onChange={handleChange}
                    step="0.01"
                    min="0"
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                    data-testid="cgst-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">SGST %</label>
                  <input
                    type="number"
                    name="sgst"
                    value={formData.sgst}
                    onChange={handleChange}
                    step="0.01"
                    min="0"
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                    data-testid="sgst-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">IGST %</label>
                  <input
                    type="number"
                    name="igst"
                    value={formData.igst}
                    onChange={handleChange}
                    step="0.01"
                    min="0"
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                    data-testid="igst-input"
                  />
                </div>
              </div>
            )}

            <div className="flex gap-3">
              <button
                type="submit"
                className="px-6 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 transition-colors"
                data-testid="save-tax-group"
              >
                {editingId ? 'Update' : 'Add'} Tax Group
              </button>
              <button
                type="button"
                onClick={resetForm}
                className="px-6 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors"
                data-testid="cancel-button"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Add Button */}
      {!showForm && (
        <button
          onClick={() => setShowForm(true)}
          className="mb-6 px-6 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 transition-colors flex items-center gap-2"
          data-testid="add-tax-group-button"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Add Tax Group
        </button>
      )}

      {/* Tax Groups List */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <table className="w-full">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-6 py-3 text-left text-sm font-semibold text-slate-600">Name</th>
              <th className="px-6 py-3 text-center text-sm font-semibold text-slate-600">CGST %</th>
              <th className="px-6 py-3 text-center text-sm font-semibold text-slate-600">SGST %</th>
              <th className="px-6 py-3 text-center text-sm font-semibold text-slate-600">IGST %</th>
              <th className="px-6 py-3 text-center text-sm font-semibold text-slate-600">Total %</th>
              <th className="px-6 py-3 text-right text-sm font-semibold text-slate-600">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {taxGroups.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-6 py-8 text-center text-slate-500">
                  No tax groups configured. Add one to get started.
                </td>
              </tr>
            ) : (
              taxGroups.map(group => (
                <tr key={group.id} className="hover:bg-slate-50" data-testid={`tax-group-${group.id}`}>
                  <td className="px-6 py-4">
                    <span className="font-medium text-slate-800">{group.name}</span>
                    {group.is_nil && (
                      <span className="ml-2 px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded-full">NIL</span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-center text-slate-600">{group.cgst}</td>
                  <td className="px-6 py-4 text-center text-slate-600">{group.sgst}</td>
                  <td className="px-6 py-4 text-center text-slate-600">{group.igst}</td>
                  <td className="px-6 py-4 text-center">
                    <span className="font-semibold text-violet-600">
                      {group.is_nil ? '0' : (group.cgst + group.sgst)}%
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <button
                      onClick={() => handleEdit(group)}
                      className="text-violet-600 hover:text-violet-700 mr-3"
                      data-testid={`edit-${group.id}`}
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(group.id)}
                      className="text-red-600 hover:text-red-700"
                      data-testid={`delete-${group.id}`}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default TaxSettings;
